import React, { useState, useEffect } from "react";
import MDBox from "components/MDBox";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MDButton from "components/MDButton";
import CircularProgress from "@mui/material/CircularProgress";
import MDTypography from "components/MDTypography";

import ReportStepper from "./ReportStepper";
import Step1ProfileSelection from "./Step1ProfileSelection";
import Step2CategorySelection from "./Step2CategorySelection";
import ReportConfiguration from "../ReportConfiguration";
import SocialPopup from "../SocialPopup";
import dayjs from "dayjs"; // [NEW] For date formatting
import { charts } from "layouts/insight-hub"; // Helper to get charts by category

function SocialReportStepper({ onComplete, onCancel, editReportData, isSaving, entitlements }) {
    // Start at step 1 (category selection) when editing, step 0 (profile selection) when creating
    const [activeStep, setActiveStep] = useState(editReportData ? 1 : 0);
    const [selectedProfiles, setSelectedProfiles] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([
        "Summary", "Audience", "Engagement", "Sentiment", "Content", "Demographics"
    ]);
    // For editing initialization
    useEffect(() => {
        if (editReportData) {
            if (editReportData.profiles) {
                setSelectedProfiles(editReportData.profiles);
            }
            if (editReportData.category) {
                // Handle legacy single category or new array
                setSelectedCategories(Array.isArray(editReportData.category) ? editReportData.category : [editReportData.category]);
            }
        }
    }, [editReportData]);

    const steps = [
        "Select Profiles",
        "Choose Chart Category",
        "Report Configuration",
    ];

    const handleNext = () => {
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        if (activeStep === 0) {
            if (onCancel) onCancel();
        } else {
            setActiveStep((prev) => prev - 1);
        }
    };

    const handleProfileSelect = (profiles) => {
        setSelectedProfiles(profiles);
    };

    const handleCategoryToggle = (categoryId) => {
        setSelectedCategories((prev) => {
            if (prev.includes(categoryId)) {
                return prev.filter(id => id !== categoryId);
            } else {
                return [...prev, categoryId];
            }
        });
    };

    // Prepare final data for ReportConfiguration
    const handleFinalSave = (configData) => {
        // 1. Get charts based on ALL selected categories + enforce Summary
        const relevantCharts = charts
            .filter((c) => c.category === "Summary" || selectedCategories.includes(c.category)) // [FIX] Always include Summary
            .map((c) => ({
                id: c.id,
                title: c.title,
                component_name: c.id
            }));

        const fullReportData = {
            report_name: configData.report_name,
            frequency: configData.frequency,
            recipient_emails: configData.recipient_emails,
            scheduleEnabled: configData.scheduleEnabled,
            config: {
                selectedProfiles: selectedProfiles.map(p => ({
                    id: p.id,
                    platform: p.platform,
                    name: p.name,
                    profilePictureUrl: p.profilePictureUrl,
                    own: p.own // [FIX] Preserve 'own' flag
                })),
                selectedCategories: selectedCategories, // ← Inside config
                visibleCharts: relevantCharts.map(c => c.id), // ← Inside config
                chartConfigs: configData.chartConfigs || {}, // ← Inside config
                period: configData.period || "last_30_days", // ← Inside config
                branding_pack_id: (configData.branding_pack_id === "system_default" || configData.branding_pack_id === "") ? null : configData.branding_pack_id, // [FIX] Avoid CastError
                target_client: configData.target_client || "", // [NEW] Target Client
            },
            sendImmediate: configData.sendImmediate || false // [NEW] Pass trigger flag
        };

        if (onComplete) {
            onComplete(fullReportData);
        }
    };

    // Toolbar state (sticky like Snapshot)
    const [navHeight, setNavHeight] = useState(75);

    // Track navbar height dynamically
    useEffect(() => {
        const navbarEl = document.querySelector("header.MuiAppBar-root");
        if (navbarEl) setNavHeight(navbarEl.offsetHeight);

        const resizeObserver = new ResizeObserver(() => {
            if (navbarEl) setNavHeight(navbarEl.offsetHeight);
        });

        if (navbarEl) resizeObserver.observe(navbarEl);
        return () => resizeObserver.disconnect();
    }, []);

    return (
        <MDBox mt={-5}>
            {/* 🧭 Stepper Bar (Sticky) */}
            <MDBox
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                width="100%"
                zIndex={100}
                bgcolor="white"
                py={1}
                px={1}
                boxShadow={1}
                sx={{
                    position: "sticky",
                    top: `${navHeight - 1}px`,
                    zIndex: 999,
                    backgroundColor: "#ffffff",
                    borderRadius: "8px",
                    gap: 2,
                    mb: 4,
                }}
            >
                <IconButton onClick={handleBack} sx={{ color: "primary.main", ml: 1 }}>
                    <ArrowBackIcon />
                </IconButton>

                <MDBox flexGrow={1} pl={2}>
                    <ReportStepper steps={steps} activeStep={activeStep} />
                </MDBox>
            </MDBox>

            {/* Step Content */}
            <MDBox pb={4}>
                {activeStep === 0 && (
                    <Step1ProfileSelection
                        selectedProfiles={selectedProfiles}
                        onSelect={handleProfileSelect}
                        maxSelection={4}
                        disabled={!!editReportData}
                    />
                )}

                {activeStep === 1 && (
                    <Step2CategorySelection
                        selectedCategories={selectedCategories}
                        onSelect={handleCategoryToggle}
                        selectedProfiles={selectedProfiles}
                    />
                )}

                {activeStep === 2 && (
                    <ReportConfiguration
                        reportData={editReportData} // Pass existing data if editing
                        defaultReportName={
                            selectedProfiles.length > 0
                                ? `${selectedProfiles[0].name} Report - ${dayjs().format("MMM DD, YYYY")}`
                                : `Social Report - ${dayjs().format("MMM DD, YYYY")}`
                        }
                        onSave={handleFinalSave}
                        onCancel={handleBack} // Config "Cancel" acts as Back
                        isStepper // [Optional] Prop to tell Config it's in a stepper
                        // [NEW] Pass context for Preview
                        selectedProfiles={selectedProfiles}
                        selectedCategories={selectedCategories}
                        isSaving={isSaving} // [FIX] Pass isSaving prop
                        entitlements={entitlements}
                    />
                )}
            </MDBox>

            {/* Animated Social Popup for Actions (Bottom) */}
            {activeStep < 2 && (
                <SocialPopup
                    selectedItems={activeStep === 0 ? selectedProfiles : selectedCategories}
                    onContinue={handleNext}
                    onBack={handleBack}
                    onClearConfirm={activeStep === 0 ? () => setSelectedProfiles([]) : null}
                    entityType={activeStep === 0 ? "profile" : "category"}
                    actionLabel="Continue"
                />
            )}

        </MDBox>
    );
}

export default SocialReportStepper;
